/**
 * Semrush Error Parser Module (Story 20.4, AC #7)
 *
 * Two-tier error mapping (carry-forward from Reddit 19.10 + Amazon Ads 19.13
 * fragile-regex hardening + Ahrefs 20.1):
 *
 *  1. **Primary** — numbered-error-code match on the plaintext body.
 *     Semrush returns plaintext error responses with HTTP 200 and shape
 *     `ERROR <code> :: <message>` (e.g. `ERROR 50 :: NOTHING FOUND`).
 *  2. **Secondary** — HTTP status code fallback for transport-level errors
 *     (401, 403, 404, 429, 5xx) that aren't structured-coded.
 *
 * Sanitization: error messages MUST flow through `sanitizeSemrushErrorMessage`
 * before reaching any log line or `AdapterError.message`. Semrush sends the
 * API key as a `key=` URL query parameter, so the literal key value can leak
 * into provider error messages, fetch error toString(), Sentry breadcrumbs,
 * etc. Round 4 Red Team Attack #1: defensive redaction is non-negotiable.
 */

import type { AdapterErrorCode } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Types
// ============================================================================

/**
 * Closed enumeration of Semrush error types. The `unknown` arm exists so the
 * parser never throws on unexpected payload shapes — the caller can still
 * surface a sanitized provider message even when no structured code matched.
 */
export type SemrushErrorType =
  | "unauthorized" // 401 / ERROR 50 :: API KEY  — invalid or revoked key
  | "forbidden" // 403 / ERROR 135 — plan doesn't include API access
  | "not_found" // 404 / ERROR 50 :: NOTHING FOUND — target/keyword not in index
  | "validation_error" // 400 — malformed request
  | "rate_limit_exceeded" // 429 / ERROR 132 / ERROR 133 — quota or RPS breach
  | "provider_unavailable" // 5xx — Semrush regional outage
  | "unknown";

export interface SemrushApiError {
  readonly type: SemrushErrorType;
  /** The Semrush numbered code when present (e.g. 50, 132, 133, 135). */
  readonly code: number | null;
  readonly message: string;
  readonly status: number | null;
  readonly retryable: boolean;
}

// ============================================================================
// Plaintext-error regex (AC #5, #7)
// ============================================================================

/**
 * Matches a Semrush plaintext error body. The body is a single line of the
 * form `ERROR <code> :: <human-readable>` — e.g.:
 *
 *   ERROR 50 :: NOTHING FOUND
 *   ERROR 132 :: API UNITS BALANCE IS ZERO
 *   ERROR 133 :: API REQUEST EXCEEDED RATE LIMIT
 *   ERROR 135 :: API IS NOT ENABLED FOR YOUR ACCOUNT
 *
 * Multiline (`/m`) so the parser still detects the error when Semrush prefixes
 * the body with a stray newline. Bounded — does NOT use `.*` greedily — so
 * adversarial 10KB error bodies cannot trigger polynomial-time backtracking.
 */
export const SEMRUSH_PLAINTEXT_ERROR_REGEX = /^ERROR (\d+) :: (.{1,500})$/m;

// ============================================================================
// Numbered-code map (AC #7)
// ============================================================================

/**
 * Map a Semrush numbered error code to our internal type + retryability.
 *
 * Source: developer.semrush.com/api/get-started/api-units-balance/ +
 * developer.semrush.com/api/introduction/api-usage-restrictions/
 * (verified 2026-05-08).
 *
 * Unknown codes default to `provider_unavailable` retryable=true — Semrush
 * has documented codes 1-19 + 50-1xx + others; exhaustively mapping is
 * fragile, defaulting to retryable transient is defensive.
 */
const NUMBERED_CODE_MAP: Record<number, { type: SemrushErrorType; retryable: boolean }> = {
  50: { type: "not_found", retryable: false }, // "NOTHING FOUND"
  132: { type: "rate_limit_exceeded", retryable: false }, // "API UNITS BALANCE IS ZERO"
  133: { type: "rate_limit_exceeded", retryable: true }, // "API REQUEST EXCEEDED RATE LIMIT"
  135: { type: "forbidden", retryable: false }, // "API IS NOT ENABLED FOR YOUR ACCOUNT"
};

/**
 * Map an HTTP status code to our internal type. Used as the fallback path
 * when no numbered code matched.
 */
const STATUS_TO_TYPE: Record<number, { type: SemrushErrorType; retryable: boolean }> = {
  400: { type: "validation_error", retryable: false },
  401: { type: "unauthorized", retryable: false },
  403: { type: "forbidden", retryable: false },
  404: { type: "not_found", retryable: false },
  429: { type: "rate_limit_exceeded", retryable: true },
  500: { type: "provider_unavailable", retryable: true },
  502: { type: "provider_unavailable", retryable: true },
  503: { type: "provider_unavailable", retryable: true },
  504: { type: "provider_unavailable", retryable: true },
};

const TYPE_TO_ADAPTER_CODE: Record<SemrushErrorType, AdapterErrorCode> = {
  unauthorized: "token_expired",
  forbidden: "permission_denied",
  not_found: "account_not_found",
  validation_error: "invalid_request",
  rate_limit_exceeded: "rate_limited",
  provider_unavailable: "provider_unavailable",
  unknown: "invalid_response",
};

function isRetryableStatus(status: number): boolean {
  return status >= 500 || status === 429;
}

// ============================================================================
// Sanitization (Round 4 Red Team Attack #1, AC #5, AC #7)
// ============================================================================

/**
 * Wraps the generic `sanitizeErrorMessage` helper from safe-fetch with two
 * additional defenses specific to Semrush:
 *
 *  1. Replace every literal occurrence of the decrypted plaintext API key
 *     with `[redacted-semrush-key]`.
 *  2. Replace every `key=<value>` query-parameter pattern in URLs with
 *     `key=[redacted-semrush-key]` — defends against the case where the
 *     URL leaks into a fetch error message (`got 401 from
 *     https://api.semrush.com/?key=abc123&...`) BEFORE the key itself was
 *     supplied as a `decryptedKey` argument.
 *
 * Real Semrush API keys are 32-64 hex chars per humanlevel.com/en/blog/seo/
 * how-to-use-semrush-api 2026-05-08. Short test stubs (single letters) would
 * otherwise match every occurrence of the letter inside human-readable text
 * and corrupt the message — require a realistic minimum key length before
 * redacting on key match.
 */
export function sanitizeSemrushErrorMessage(
  message: string,
  decryptedKey?: string
): string {
  // Run the generic sanitizer FIRST — it strips Bearer tokens, long
  // base64 strings, and `?key=`/`&key=` URL params (replacing with
  // `[REDACTED]`) plus other generic OAuth-shape secrets.
  let sanitized = sanitizeErrorMessage(message);

  // Then apply Semrush-specific redactions on top:

  // (1) Replace the generic sanitizer's `key=[REDACTED]` output with our
  // canonical Semrush token. Also catches any `key=<value>` URL form that
  // the generic regex missed (e.g. `key=` at the start of the string with
  // no leading `?` or `&`, or URL-encoded `key%3D<value>`).
  //
  // **Code Review Pass 1 (Critical) — bounded match preconditions:** the
  // regex MUST require the `key=` token to be preceded by a URL-query
  // separator (`?`, `&`) OR appear at the literal start of the string OR
  // immediately after a whitespace boundary. This prevents corrupting
  // unrelated prose like `"foreign key=fk_user_id constraint failed"` or
  // SQL diagnostics that legitimately contain `key=...` substrings.
  sanitized = sanitized.replace(/key=\[REDACTED\]/g, "key=[redacted-semrush-key]");
  sanitized = sanitized.replace(/(^|[?&\s])key=[^&\s"'<>]{1,2048}/g, "$1key=[redacted-semrush-key]");
  // URL-encoded form: `key%3D<value>` (the `=` sign is `%3D`). Same precondition.
  sanitized = sanitized.replace(/(^|[?&\s])key%3D[^&\s"'<>]{1,2048}/gi, "$1key%3D[redacted-semrush-key]");

  // (2) Exact-key replacement — defends against the case where the
  // decrypted plaintext key leaked into a non-URL position (e.g. the
  // provider echoed the key value back in a malformed-request error body).
  if (decryptedKey && decryptedKey.length >= 16) {
    const escaped = decryptedKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized.replace(new RegExp(escaped, "g"), "[redacted-semrush-key]");
  }
  return sanitized;
}

// ============================================================================
// Two-tier parser (AC #7)
// ============================================================================

/**
 * Parse a Semrush response into a structured error.
 *
 * @param body - The raw response body (CSV success OR plaintext error).
 * @param httpStatus - HTTP status code from the response.
 * @param decryptedKey - Optional plaintext key for sanitization.
 * @returns A structured SemrushApiError, or null if the response was a
 *   CSV success body and HTTP 2xx.
 */
export function parseSemrushApiError(
  body: string,
  httpStatus: number,
  decryptedKey?: string
): SemrushApiError | null {
  // Tier 1 — numbered-error-code match (works on HTTP 200 plaintext errors).
  const match = SEMRUSH_PLAINTEXT_ERROR_REGEX.exec(body);
  if (match) {
    const code = Number.parseInt(match[1]!, 10);
    const rawMessage = match[2]!.trim();
    const sanitizedMessage = sanitizeSemrushErrorMessage(
      `Semrush error ${code}: ${rawMessage}`,
      decryptedKey
    );

    // **Code Review Pass 2 (Critical) — code 50 sub-routing.**
    // Semrush emits `ERROR 50 :: API KEY <details>` for invalid/revoked
    // keys AND `ERROR 50 :: NOTHING FOUND` for "this target is not in our
    // index" (target / keyword absence). Both share numeric code 50, but
    // they map to different AdapterErrorCodes:
    //   - API KEY → unauthorized (cron `mark_revoked_notify_user`,
    //     connect dialog `invalid_api_key` 400 with "verify your key" copy)
    //   - NOTHING FOUND → not_found / account_not_found (transient, the
    //     target may simply be too new or out-of-index)
    // Without this split, a revoked key during cron health-check would be
    // mis-classified as `account_not_found` (silent — no rotation
    // notification ever fires) and a connect attempt with a wrong key
    // would surface a generic "validation failed" instead of the spec's
    // "verify the key" copy.
    if (code === 50 && /^\s*API KEY\b/i.test(rawMessage)) {
      return {
        type: "unauthorized",
        code,
        message: sanitizedMessage,
        status: httpStatus,
        retryable: false,
      };
    }

    const mapping = NUMBERED_CODE_MAP[code];
    if (mapping) {
      return {
        type: mapping.type,
        code,
        message: sanitizedMessage,
        status: httpStatus,
        retryable: mapping.retryable,
      };
    }
    // Unknown numbered code — default to retryable transient.
    return {
      type: "provider_unavailable",
      code,
      message: sanitizedMessage,
      status: httpStatus,
      retryable: true,
    };
  }

  // Tier 2 — HTTP status fallback (no numbered code in body).
  if (httpStatus >= 200 && httpStatus < 300) {
    return null; // Not an error — caller treats body as CSV.
  }

  const sanitized = sanitizeSemrushErrorMessage(
    `Semrush API error: HTTP ${httpStatus}`,
    decryptedKey
  );
  const mapping = STATUS_TO_TYPE[httpStatus];
  if (mapping) {
    return {
      type: mapping.type,
      code: null,
      message: sanitized,
      status: httpStatus,
      retryable: mapping.retryable,
    };
  }
  return {
    type: "unknown",
    code: null,
    message: sanitized,
    status: httpStatus,
    retryable: isRetryableStatus(httpStatus),
  };
}

/**
 * Map a `SemrushErrorType` to the canonical `AdapterErrorCode` used by the
 * adapter contract.
 */
export function semrushErrorTypeToAdapterCode(type: SemrushErrorType): AdapterErrorCode {
  return TYPE_TO_ADAPTER_CODE[type];
}

// ============================================================================
// Throwable wrapper
// ============================================================================

/**
 * Throwable error class used by helpers to surface a parsed Semrush error to
 * the adapter. Mirrors `AhrefsApiCallError` shape so the adapter dispatch
 * logic stays uniform.
 */
export class SemrushApiCallError extends Error {
  readonly status: number;
  readonly type: SemrushErrorType;
  readonly code: number | null;
  readonly retryable: boolean;

  constructor(parsed: SemrushApiError) {
    super(parsed.message);
    this.name = "SemrushApiCallError";
    this.status = parsed.status ?? 0;
    this.type = parsed.type;
    this.code = parsed.code;
    this.retryable = parsed.retryable;
  }
}
